import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchVideoTranscription,
  requestVideoTranscription,
} from 'dashboard/api/videoTranscription';

const POLL_MS = 3000;
const POLL_LIMIT = 100; // About five minutes, the server-side lease.
const FINAL = ['complete', 'failed', 'skipped'];

// gpt-transcribe reports ISO 639-1 codes.
const CODES = {
  ar: '阿拉伯语',
  ca: '加泰罗尼亚语',
  cs: '捷克语',
  da: '丹麦语',
  de: '德语',
  el: '希腊语',
  en: '英语',
  es: '西班牙语',
  fi: '芬兰语',
  fr: '法语',
  he: '希伯来语',
  hi: '印地语',
  hu: '匈牙利语',
  id: '印尼语',
  it: '意大利语',
  ja: '日语',
  ko: '韩语',
  nb: '挪威语',
  nl: '荷兰语',
  no: '挪威语',
  pl: '波兰语',
  pt: '葡萄牙语',
  ro: '罗马尼亚语',
  ru: '俄语',
  sk: '斯洛伐克语',
  sv: '瑞典语',
  th: '泰语',
  tr: '土耳其语',
  uk: '乌克兰语',
  vi: '越南语',
  zh: '中文',
};
// Results stored earlier by whisper-1 name the language in English.
const LANGUAGES = {
  arabic: '阿拉伯语',
  catalan: '加泰罗尼亚语',
  chinese: '中文',
  czech: '捷克语',
  danish: '丹麦语',
  dutch: '荷兰语',
  english: '英语',
  finnish: '芬兰语',
  french: '法语',
  german: '德语',
  greek: '希腊语',
  hebrew: '希伯来语',
  hindi: '印地语',
  hungarian: '匈牙利语',
  indonesian: '印尼语',
  italian: '意大利语',
  japanese: '日语',
  korean: '韩语',
  norwegian: '挪威语',
  polish: '波兰语',
  portuguese: '葡萄牙语',
  romanian: '罗马尼亚语',
  russian: '俄语',
  slovak: '斯洛伐克语',
  spanish: '西班牙语',
  swedish: '瑞典语',
  thai: '泰语',
  turkish: '土耳其语',
  ukrainian: '乌克兰语',
  vietnamese: '越南语',
};

export const languageLabel = language => {
  const key = String(language || '').toLowerCase();
  return (
    CODES[key] || CODES[key.split('-')[0]] || LANGUAGES[key] || language || '未知语言'
  );
};

// No provider error text reaches the UI; every failure has a fixed label.
function failureLabel(code) {
  if (code === 'no_audio') return '视频没有声音。';
  if (code === 'no_speech') return '未识别到语音。';
  if (code === 'media_unavailable') return '视频文件不可用。';
  if (code === 'translation_failed') return '已识别原文，翻译失败，可再试一次。';
  return '识别失败，请重试。';
}

const pick = (value, snake, camel) => value?.[snake] ?? value?.[camel];

// Message payloads may arrive with snake_case or camelCase keys.
function normalize(value, videoId) {
  if (!value || typeof value !== 'object') return null;
  const attachmentId = pick(value, 'attachment_id', 'attachmentId');
  if (attachmentId !== undefined && attachmentId !== videoId) return null;
  const leaseUntil = Number(pick(value, 'lease_until', 'leaseUntil')) || 0;
  let status = value.status;
  if (status === 'pending' && attachmentId !== undefined && leaseUntil * 1000 <= Date.now()) {
    status = 'failed';
  }
  return {
    status,
    code: typeof value.code === 'string' ? value.code : '',
    language: typeof value.language === 'string' ? value.language : '',
    languages: Array.isArray(value.languages)
      ? value.languages.filter(code => typeof code === 'string')
      : [],
    transcript: typeof value.transcript === 'string' ? value.transcript : '',
    translation:
      typeof value.translation === 'string' ? value.translation : '',
  };
}

export function useVideoTranscription(context) {
  const route = useRoute();
  const local = ref(null);
  const busy = ref(false);
  const expanded = ref(false);
  let timer = null;
  let polls = 0;
  let generation = 0;
  let disposed = false;

  const attributes = computed(() => context.contentAttributes.value || {});
  const video = computed(() => context.attachments.value?.[0]);
  const eligible = computed(
    () =>
      [0, 1].includes(context.messageType.value) &&
      !context.isPrivate?.value &&
      !context.private?.value &&
      !attributes.value.deleted &&
      Boolean(video.value?.id)
  );
  const stored = computed(() =>
    normalize(
      attributes.value.bcwVideoTranscriptV1 ||
        attributes.value.bcw_video_transcript_v1,
      video.value?.id
    )
  );
  // The stored projection as it was when this view last sent a request.
  const requestedFrom = ref(null);
  // Until a live update changes the stored projection, the server's latest
  // answer to this view's own request wins; then the projection is
  // authoritative once final, and a polled result covers a missed update.
  const state = computed(() => {
    if (busy.value) return { status: 'pending' };
    if (local.value && JSON.stringify(stored.value) === requestedFrom.value) {
      return local.value;
    }
    if (stored.value && FINAL.includes(stored.value.status)) return stored.value;
    if (local.value && FINAL.includes(local.value.status)) return local.value;
    return stored.value || local.value || { status: 'idle' };
  });
  const pending = computed(() => state.value.status === 'pending');
  const complete = computed(() => state.value.status === 'complete');
  const retranslate = computed(
    () => complete.value && state.value.code === 'translation_failed'
  );
  const canRequest = computed(
    () =>
      eligible.value &&
      !pending.value &&
      (['idle', 'failed'].includes(state.value.status) || retranslate.value)
  );
  const notice = computed(() => {
    if (pending.value) return '正在识别语音并翻译…';
    if (state.value.status === 'failed' || state.value.code) {
      return failureLabel(state.value.code);
    }
    return '';
  });
  // Mixed speech can report several languages; show each once.
  const heading = computed(() => {
    if (!complete.value || !state.value.transcript) return '';
    const codes = state.value.languages?.length
      ? state.value.languages
      : [state.value.language];
    const names = [...new Set(codes.filter(Boolean).map(languageLabel))];
    return `语音识别 · ${names.join('、') || '未知语言'}`;
  });
  const translation = computed(() =>
    state.value.translation !== state.value.transcript
      ? state.value.translation
      : ''
  );

  const scope = () => ({
    accountId: route.params.accountId,
    conversationId: context.conversationId.value,
    messageId: context.id.value,
  });

  function stopPolling() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function poll() {
    stopPolling();
    if (disposed || !pending.value || polls >= POLL_LIMIT) return;
    const current = generation;
    timer = setTimeout(async () => {
      polls += 1;
      try {
        const result = normalize(await fetchVideoTranscription(scope()));
        if (!disposed && current === generation && result) local.value = result;
      } catch {
        // A transient read failure keeps polling until the lease runs out.
      }
      poll();
    }, POLL_MS);
  }

  watch(pending, value => (value ? poll() : stopPolling()), {
    immediate: true,
  });
  // Reset only when this view shows another message or video. Live updates
  // replace the attachments array without changing these ids, and must not
  // drop the request in flight.
  watch([() => context.id.value, () => video.value?.id], () => {
    generation += 1;
    busy.value = false;
    local.value = null;
    requestedFrom.value = null;
    expanded.value = false;
  });

  async function request() {
    if (!canRequest.value) return;
    const current = generation;
    requestedFrom.value = JSON.stringify(stored.value);
    busy.value = true;
    polls = 0;
    try {
      const result = normalize(await requestVideoTranscription(scope()));
      if (!disposed && current === generation) local.value = result;
    } catch {
      if (!disposed && current === generation) {
        local.value = { status: 'failed', code: 'provider_unavailable' };
      }
    } finally {
      if (!disposed && current === generation) busy.value = false;
    }
  }

  // One control: show/hide a finished or running result (videos are processed
  // on arrival), otherwise start recognition.
  async function toggle() {
    if ((complete.value && !retranslate.value) || pending.value) {
      expanded.value = !expanded.value;
      return;
    }
    expanded.value = true;
    await request();
  }

  onUnmounted(() => {
    disposed = true;
    generation += 1;
    stopPolling();
  });

  return {
    eligible,
    state,
    pending,
    complete,
    expanded,
    notice,
    heading,
    translation,
    toggle,
  };
}
